import { initializeApp } from "https://www.gstatic.com/firebasejs/11.6.1/firebase-app.js";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification
} from "https://www.gstatic.com/firebasejs/11.6.1/firebase-auth.js";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  limit,
  getDocs,
  addDoc,
  serverTimestamp,
  orderBy,
  onSnapshot,
  startAfter
} from "https://www.gstatic.com/firebasejs/11.6.1/firebase-firestore.js";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from "https://www.gstatic.com/firebasejs/11.6.1/firebase-storage.js";
import {
  getFunctions,
  httpsCallable
} from "https://www.gstatic.com/firebasejs/11.6.1/firebase-functions.js";

const challengeRoot = document.querySelector("[data-challenge-root]");

if (!challengeRoot) {
  console.warn("Challenge root not found.");
} else {
  const firebaseConfig = {
    apiKey: challengeRoot.dataset.firebaseApiKey,
    authDomain: challengeRoot.dataset.firebaseAuthDomain,
    projectId: challengeRoot.dataset.firebaseProjectId,
    storageBucket: challengeRoot.dataset.firebaseStorageBucket,
    messagingSenderId: challengeRoot.dataset.firebaseMessagingSenderId,
    appId: challengeRoot.dataset.firebaseAppId
  };

  const challengeSlug = challengeRoot.dataset.challengeSlug;

  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);
  const functions = getFunctions(app, "us-central1");
  const castVoteCallable = httpsCallable(functions, "castVote");

  const GALLERY_PAGE_SIZE = 12;
  const LEADERBOARD_LIMIT = 5;

  let currentChallenge = null;
  let currentUserSubmission = null;
  let galleryLastVisible = null;
  let galleryHasMore = false;
  let galleryLoading = false;
  let leaderboardUnsubscribe = null;
  let votingInProgressIds = new Set();
  let pendingModalAction = null;

  const els = {
    challengeStatus: challengeRoot.querySelector("[data-challenge-status]"),
    firebaseStatus: challengeRoot.querySelector("[data-firebase-status]"),
    challengeTitle: challengeRoot.querySelector("[data-challenge-title]"),
    challengeShortDescription: challengeRoot.querySelector("[data-challenge-short-description]"),
    challengeDates: challengeRoot.querySelector("[data-challenge-dates]"),
    challengePrize: challengeRoot.querySelector("[data-challenge-prize]"),
    challengeRules: challengeRoot.querySelector("[data-challenge-rules]"),

    appShell: challengeRoot.querySelector("[data-app-shell]"),
    accountPanel: challengeRoot.querySelector("[data-account-panel]"),
    accountEmail: challengeRoot.querySelector("[data-account-email]"),

    firebaseAuthPanel: challengeRoot.querySelector("[data-firebase-auth-panel]"),
    firebaseEmail: challengeRoot.querySelector("[data-firebase-email]"),
    firebasePassword: challengeRoot.querySelector("[data-firebase-password]"),
    firebaseLoginBtn: challengeRoot.querySelector("[data-firebase-login]"),
    firebaseSignupBtn: challengeRoot.querySelector("[data-firebase-signup]"),
    firebaseLogoutBtn: challengeRoot.querySelector("[data-firebase-logout]"),
    firebaseMessage: challengeRoot.querySelector("[data-firebase-message]"),
    passwordResetBtn: challengeRoot.querySelector("[data-password-reset]"),

    authModal: challengeRoot.querySelector("[data-auth-modal]"),
    authModalTitle: challengeRoot.querySelector("[data-auth-modal-title]"),
    authModalMessage: challengeRoot.querySelector("[data-auth-modal-message]"),
    authModalConfirm: challengeRoot.querySelector("[data-auth-modal-confirm]"),
    authModalCloseBtns: challengeRoot.querySelectorAll("[data-auth-modal-close]"),

    submissionForm: challengeRoot.querySelector("[data-submission-form]"),
    submissionName: challengeRoot.querySelector("[data-submission-name]"),
    submissionInstagram: challengeRoot.querySelector("[data-submission-instagram]"),
    submissionTitle: challengeRoot.querySelector("[data-submission-title]"),
    submissionDescription: challengeRoot.querySelector("[data-submission-description]"),
    submissionFile: challengeRoot.querySelector("[data-submission-file]"),
    submissionMessage: challengeRoot.querySelector("[data-submission-message]"),
    submissionSubmit: challengeRoot.querySelector("[data-submission-submit]"),

    userSubmissionState: challengeRoot.querySelector("[data-user-submission-state]"),

    leaderboardList: challengeRoot.querySelector("[data-leaderboard-list]"),
    leaderboardMessage: challengeRoot.querySelector("[data-leaderboard-message]"),

    galleryGrid: challengeRoot.querySelector("[data-gallery-grid]"),
    galleryMessage: challengeRoot.querySelector("[data-gallery-message]"),
    galleryLoadMore: challengeRoot.querySelector("[data-gallery-load-more]"),
    voteMessage: challengeRoot.querySelector("[data-vote-message]")
  };

  function setText(el, value) {
    if (el) el.textContent = value || "";
  }

  function setMessage(el, message, isError = false) {
    if (!el) return;
    el.textContent = message || "";
    el.dataset.state = isError ? "error" : "success";
  }

  function getFriendlyAuthError(error) {
    const code = error?.code || "";

    switch (code) {
      case "auth/user-not-found":
      case "auth/invalid-credential":
        return "No TLWC Community Account was found with that email and password.";
      case "auth/wrong-password":
        return "The password you entered is incorrect.";
      case "auth/email-already-in-use":
        return "A TLWC Community Account already exists for this email. Try logging in instead.";
      case "auth/invalid-email":
        return "Enter a valid email address.";
      case "auth/weak-password":
        return "Password must be at least 6 characters.";
      case "auth/missing-password":
        return "Enter your password.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a moment and try again.";
      case "auth/network-request-failed":
        return "Network error. Check your connection and try again.";
      default:
        return "Something went wrong. Please try again.";
    }
  }

  function getEmailAndPassword() {
    return {
      email: els.firebaseEmail?.value?.trim() || "",
      password: els.firebasePassword?.value || ""
    };
  }

  function openAuthModal({ title, message, confirmText, action }) {
    if (!els.authModal) return;

    pendingModalAction = action;

    setText(els.authModalTitle, title || "TLWC Community Account");
    setText(
      els.authModalMessage,
      message || "You are about to create a TLWC Community Account."
    );

    if (els.authModalConfirm) {
      els.authModalConfirm.textContent = confirmText || "Create Account";
    }

    els.authModal.hidden = false;
    document.body.classList.add("tlwc-modal-open");
  }

  function closeAuthModal() {
    if (!els.authModal) return;

    els.authModal.hidden = true;
    document.body.classList.remove("tlwc-modal-open");
    pendingModalAction = null;
  }

  function escapeHtml(value = "") {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  function formatDateRange(start, end) {
    if (!start || !end) return "";

    const startDate = start?.toDate ? start.toDate() : new Date(start);
    const endDate = end?.toDate ? end.toDate() : new Date(end);

    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });

    return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
  }

  function formatDateTime(value) {
    if (!value) return "";

    const date = value?.toDate ? value.toDate() : new Date(value);

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    }).format(date);
  }

  function slugifyFileName(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9.-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function isChallengeSubmissionOpen() {
    if (!currentChallenge) return false;
    if (currentChallenge.allowSubmissions !== true) return false;
    if (currentChallenge.status !== "active") return false;

    const now = new Date();
    const start = currentChallenge.submissionStartAt?.toDate ? currentChallenge.submissionStartAt.toDate() : null;
    const end = currentChallenge.submissionEndAt?.toDate ? currentChallenge.submissionEndAt.toDate() : null;

    if (!start || !end) return false;

    return now >= start && now <= end;
  }

  function isChallengeVotingOpen() {
    if (!currentChallenge) return false;
    if (currentChallenge.allowVoting !== true) return false;
    if (currentChallenge.status !== "active") return false;

    const now = new Date();
    const start = currentChallenge.votingStartAt?.toDate ? currentChallenge.votingStartAt.toDate() : null;
    const end = currentChallenge.votingEndAt?.toDate ? currentChallenge.votingEndAt.toDate() : null;

    if (!start || !end) return false;

    return now >= start && now <= end;
  }

  function canCurrentUserVote() {
    return !!auth.currentUser && isChallengeVotingOpen();
  }

  function updateSubmissionAvailability() {
    const canSubmit =
      !!auth.currentUser &&
      isChallengeSubmissionOpen() &&
      !currentUserSubmission;

    if (els.submissionSubmit) {
      els.submissionSubmit.disabled = !canSubmit;
    }

    if (!els.submissionMessage) return;

    if (!auth.currentUser) {
      setMessage(els.submissionMessage, "Sign in with your TLWC Community Account to submit your entry.", true);
      return;
    }

    if (!currentChallenge) {
      setMessage(els.submissionMessage, "Challenge data is still loading.", true);
      return;
    }

    if (!isChallengeSubmissionOpen()) {
      setMessage(els.submissionMessage, "Submissions are currently closed for this challenge.", true);
      return;
    }

    if (currentUserSubmission) {
      setMessage(els.submissionMessage, "You have already submitted one photo for this challenge.", true);
      return;
    }

    setMessage(els.submissionMessage, "You are eligible to submit one JPEG or PNG entry.", false);
  }

  function renderUserSubmission() {
    if (!els.userSubmissionState) return;

    if (!currentUserSubmission) {
      els.userSubmissionState.innerHTML = "<p>No submission found yet for this challenge.</p>";
      return;
    }

    const createdAtText = currentUserSubmission.createdAt
      ? formatDateTime(currentUserSubmission.createdAt)
      : "Pending timestamp";

    els.userSubmissionState.innerHTML = `
      <div class="tlwc-submission-card">
        <img src="${escapeHtml(currentUserSubmission.imageUrl || "")}" alt="${escapeHtml(currentUserSubmission.photoTitle || "Submitted photo")}">
        <div>
          <p><span class="tlwc-status-badge">${escapeHtml(currentUserSubmission.status || "pending")}</span></p>
          <h4>${escapeHtml(currentUserSubmission.photoTitle || "")}</h4>
          <p>${escapeHtml(currentUserSubmission.photoDescription || "")}</p>
          <p><strong>Instagram:</strong> ${escapeHtml(currentUserSubmission.instagramHandle || "")}</p>
          <p><strong>Submitted:</strong> ${escapeHtml(createdAtText)}</p>
          <p><strong>Votes:</strong> ${currentUserSubmission.voteCount ?? 0}</p>
        </div>
      </div>
    `;
  }

  function renderLeaderboard(entries) {
    if (!els.leaderboardList) return;

    if (!entries.length) {
      els.leaderboardList.innerHTML = "";
      setText(els.leaderboardMessage, "No approved entries have been added yet.");
      return;
    }

    setText(els.leaderboardMessage, "Live results update automatically as approved entries and votes change.");

    els.leaderboardList.innerHTML = entries
      .map((entry, index) => {
        const name = entry.displayName || "Anonymous";
        const title = entry.photoTitle || "Untitled photo";
        const instagram = entry.instagramHandle || "";
        const votes = entry.voteCount ?? 0;
        const imageUrl = entry.imageUrl || "";

        return `
          <article class="tlwc-leaderboard-item">
            <div class="tlwc-leaderboard-rank">#${index + 1}</div>
            <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}">
            <div class="tlwc-leaderboard-content">
              <h4>${escapeHtml(title)}</h4>
              <p>By ${escapeHtml(name)}${instagram ? ` · ${escapeHtml(instagram)}` : ""}</p>
            </div>
            <div class="tlwc-vote-pill">${votes} vote${votes === 1 ? "" : "s"}</div>
          </article>
        `;
      })
      .join("");
  }

  function getVoteButtonLabel(entry) {
    if (votingInProgressIds.has(entry.id)) return "Voting...";
    if (!auth.currentUser) return "Sign in to vote";
    if (!isChallengeVotingOpen()) return "Voting closed";
    return "Vote for this photo";
  }

  function renderGalleryCards(entries, append = false) {
    if (!els.galleryGrid) return;

    const html = entries
      .map((entry) => {
        const title = entry.photoTitle || "Untitled photo";
        const description = entry.photoDescription || "";
        const name = entry.displayName || "Anonymous";
        const instagram = entry.instagramHandle || "";
        const votes = entry.voteCount ?? 0;
        const imageUrl = entry.imageUrl || "";
        const createdAt = entry.createdAt ? formatDateTime(entry.createdAt) : "";
        const voteDisabled = !canCurrentUserVote() || votingInProgressIds.has(entry.id);

        return `
          <article class="tlwc-gallery-card" data-gallery-card="${escapeHtml(entry.id)}">
            <div class="tlwc-gallery-image-wrap">
              <img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(title)}" loading="lazy">
            </div>
            <div class="tlwc-gallery-card-body">
              <div class="tlwc-gallery-card-top">
                <h4>${escapeHtml(title)}</h4>
                <span class="tlwc-vote-pill">${votes} vote${votes === 1 ? "" : "s"}</span>
              </div>

              <p class="tlwc-gallery-byline">By ${escapeHtml(name)}${instagram ? ` · ${escapeHtml(instagram)}` : ""}</p>
              <p class="tlwc-gallery-description">${escapeHtml(description)}</p>
              <p class="tlwc-gallery-date">${escapeHtml(createdAt)}</p>

              <div class="tlwc-gallery-actions">
                <button
                  class="tlwc-button"
                  type="button"
                  data-vote-button
                  data-submission-id="${escapeHtml(entry.id)}"
                  ${voteDisabled ? "disabled" : ""}
                >
                  ${escapeHtml(getVoteButtonLabel(entry))}
                </button>
              </div>
            </div>
          </article>
        `;
      })
      .join("");

    if (append) {
      els.galleryGrid.insertAdjacentHTML("beforeend", html);
    } else {
      els.galleryGrid.innerHTML = html;
    }
  }

  function rerenderVoteButtons() {
    const buttons = challengeRoot.querySelectorAll("[data-vote-button]");

    buttons.forEach((button) => {
      const submissionId = button.dataset.submissionId;
      const isBusy = votingInProgressIds.has(submissionId);

      button.disabled = !canCurrentUserVote() || isBusy;

      if (isBusy) {
        button.textContent = "Voting...";
      } else if (!auth.currentUser) {
        button.textContent = "Sign in to vote";
      } else if (!isChallengeVotingOpen()) {
        button.textContent = "Voting closed";
      } else {
        button.textContent = "Vote for this photo";
      }
    });
  }

  function updateGalleryLoadMoreButton() {
    if (!els.galleryLoadMore) return;

    els.galleryLoadMore.hidden = !galleryHasMore;
    els.galleryLoadMore.disabled = galleryLoading;
    els.galleryLoadMore.textContent = galleryLoading ? "Loading..." : "Load more entries";
  }

  async function loadGalleryInitial() {
    if (!els.galleryGrid) return;

    try {
      galleryLoading = true;
      updateGalleryLoadMoreButton();
      setText(els.galleryMessage, "Loading approved entries...");

      const submissionsRef = collection(db, "submissions");

      const galleryQuery = query(
        submissionsRef,
        where("challengeSlug", "==", challengeSlug),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc"),
        limit(GALLERY_PAGE_SIZE)
      );

      const snap = await getDocs(galleryQuery);

      if (snap.empty) {
        els.galleryGrid.innerHTML = "";
        galleryLastVisible = null;
        galleryHasMore = false;
        setText(els.galleryMessage, "No approved entries yet. Approved photos will appear here.");
        return;
      }

      const entries = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      renderGalleryCards(entries, false);

      galleryLastVisible = snap.docs[snap.docs.length - 1];
      galleryHasMore = snap.docs.length === GALLERY_PAGE_SIZE;

      setText(els.galleryMessage, `Showing ${entries.length} approved entr${entries.length === 1 ? "y" : "ies"}.`);

      rerenderVoteButtons();
    } catch (error) {
      console.error(error);
      setText(els.galleryMessage, "Could not load the gallery. Check Firestore indexes and try again.");
    } finally {
      galleryLoading = false;
      updateGalleryLoadMoreButton();
    }
  }

  async function loadGalleryMore() {
    if (!galleryLastVisible || galleryLoading) return;

    try {
      galleryLoading = true;
      updateGalleryLoadMoreButton();
      setText(els.galleryMessage, "Loading more approved entries...");

      const submissionsRef = collection(db, "submissions");

      const galleryQuery = query(
        submissionsRef,
        where("challengeSlug", "==", challengeSlug),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc"),
        startAfter(galleryLastVisible),
        limit(GALLERY_PAGE_SIZE)
      );

      const snap = await getDocs(galleryQuery);

      if (snap.empty) {
        galleryHasMore = false;
        setText(els.galleryMessage, "You’ve reached the end of the approved entries.");
        return;
      }

      const entries = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      renderGalleryCards(entries, true);

      galleryLastVisible = snap.docs[snap.docs.length - 1];
      galleryHasMore = snap.docs.length === GALLERY_PAGE_SIZE;

      setText(els.galleryMessage, `Loaded ${entries.length} more approved entr${entries.length === 1 ? "y" : "ies"}.`);

      rerenderVoteButtons();
    } catch (error) {
      console.error(error);
      setText(els.galleryMessage, "Could not load more entries. Check Firestore indexes and try again.");
    } finally {
      galleryLoading = false;
      updateGalleryLoadMoreButton();
    }
  }

  function watchLeaderboard() {
    if (leaderboardUnsubscribe) {
      leaderboardUnsubscribe();
      leaderboardUnsubscribe = null;
    }

    const submissionsRef = collection(db, "submissions");

    const leaderboardQuery = query(
      submissionsRef,
      where("challengeSlug", "==", challengeSlug),
      where("status", "==", "approved"),
      orderBy("voteCount", "desc"),
      orderBy("createdAt", "desc"),
      limit(LEADERBOARD_LIMIT)
    );

    leaderboardUnsubscribe = onSnapshot(
      leaderboardQuery,
      (snap) => {
        const entries = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        renderLeaderboard(entries);
      },
      (error) => {
        console.error(error);
        setText(els.leaderboardMessage, "Could not load the leaderboard. Check Firestore indexes and try again.");
      }
    );
  }

  async function loadChallenge() {
    try {
      const challengeRef = doc(db, "challenges", challengeSlug);
      const challengeSnap = await getDoc(challengeRef);

      if (!challengeSnap.exists()) {
        setText(els.challengeStatus, "Challenge not found.");
        return;
      }

      const data = challengeSnap.data();
      currentChallenge = data;

      setText(els.challengeTitle, data.title || "Challenge");
      setText(els.challengeShortDescription, data.shortDescription || "");

      const dateRange = formatDateRange(data.submissionStartAt, data.submissionEndAt);
      const votingEnd = data.votingEndAt?.toDate
        ? new Intl.DateTimeFormat("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
          }).format(data.votingEndAt.toDate())
        : "";

      setText(
        els.challengeDates,
        `${dateRange}${votingEnd ? ` | Voting ends ${votingEnd}` : ""}`
      );

      setText(
        els.challengePrize,
        `${data.prizeTitle || ""}${data.prizeDescription ? ": " + data.prizeDescription : ""}`
      );

      if (els.challengeRules) {
        els.challengeRules.innerHTML = "";

        (data.rules || []).forEach((rule) => {
          const li = document.createElement("li");
          li.textContent = rule;
          els.challengeRules.appendChild(li);
        });
      }

      setText(
  els.challengeStatus,
  `Challenge: ${(data.status || "unknown").charAt(0).toUpperCase() + (data.status || "unknown").slice(1)}`
);

      await loadGalleryInitial();
      watchLeaderboard();
      updateSubmissionAvailability();
      rerenderVoteButtons();
    } catch (error) {
      console.error(error);
      setText(els.challengeStatus, "Failed to load challenge.");
    }
  }

  function updateAuthPanels(user) {
    if (!els.firebaseStatus || !els.firebaseAuthPanel || !els.accountPanel || !els.appShell) return;

    if (user) {
      setText(els.firebaseStatus, `Signed in with TLWC Community Account`);

      els.firebaseAuthPanel.hidden = true;
      els.accountPanel.hidden = false;
      els.appShell.hidden = false;

      if (els.accountEmail) {
        els.accountEmail.textContent = user.email
          ? `Account email: ${user.email}`
          : "TLWC Community Account connected.";
      }

      if (els.firebaseEmail && !els.firebaseEmail.value) {
        els.firebaseEmail.value = user.email || "";
      }
    } else {
      setText(els.firebaseStatus, "Sign in to submit and vote.");

      els.firebaseAuthPanel.hidden = false;
      els.accountPanel.hidden = true;
      els.appShell.hidden = true;

      currentUserSubmission = null;
      renderUserSubmission();
    }

    updateSubmissionAvailability();
    rerenderVoteButtons();
  }

  async function createChallengeAccountFromInputs() {
    const { email, password } = getEmailAndPassword();

    if (!email || !password) {
      setMessage(els.firebaseMessage, "Enter your email and password to create a TLWC Community Account.", true);
      return;
    }

    try {
      setMessage(els.firebaseMessage, "Creating your TLWC Community Account...", false);

      const credential = await createUserWithEmailAndPassword(auth, email, password);

      if (credential.user) {
        try {
          await sendEmailVerification(credential.user);
          setMessage(
            els.firebaseMessage,
            "TLWC Community Account created. Please check your email to verify your account.",
            false
          );
        } catch (verificationError) {
          console.error(verificationError);
          setMessage(
            els.firebaseMessage,
            "TLWC Community Account created, but the verification email could not be sent. You can still continue for now.",
            false
          );
        }
      }
    } catch (error) {
      console.error(error);
      setMessage(els.firebaseMessage, getFriendlyAuthError(error), true);
    }
  }

  async function handleFirebaseLogin() {
    const { email, password } = getEmailAndPassword();

    if (!email || !password) {
      setMessage(els.firebaseMessage, "Enter your email and password.", true);
      return;
    }

    try {
      setMessage(els.firebaseMessage, "Signing in...", false);
      await signInWithEmailAndPassword(auth, email, password);
      setMessage(els.firebaseMessage, "Signed in successfully.", false);
    } catch (error) {
      console.error(error);

      if (error?.code === "auth/user-not-found" || error?.code === "auth/invalid-credential") {
        setMessage(els.firebaseMessage, "No TLWC Community Account was found with that email.", true);

        openAuthModal({
          title: "Create TLWC Community Account?",
          message: "No TLWC Community Account was found with that email. Would you like to create one now using the email and password you entered?",
          confirmText: "Create Account",
          action: createChallengeAccountFromInputs
        });

        return;
      }

      setMessage(els.firebaseMessage, getFriendlyAuthError(error), true);
    }
  }

  function handleFirebaseSignupPrompt() {
    const { email, password } = getEmailAndPassword();

    if (!email || !password) {
      setMessage(els.firebaseMessage, "Enter your email and password before creating a TLWC Community Account.", true);
      return;
    }

    openAuthModal({
      title: "Create TLWC Community Account",
      message: "You are about to create a TLWC Community Account. This account is used for challenge submissions, voting, and future TLWC community features.",
      confirmText: "Create Account",
      action: createChallengeAccountFromInputs
    });
  }

  async function handlePasswordReset() {
    const email = els.firebaseEmail?.value?.trim() || "";

    if (!email) {
      setMessage(els.firebaseMessage, "Enter your email first, then click Forgot password.", true);
      return;
    }

    try {
      setMessage(els.firebaseMessage, "Sending password reset email...", false);
      await sendPasswordResetEmail(auth, email);
      setMessage(els.firebaseMessage, "Password reset email sent. Check your inbox.", false);
    } catch (error) {
      console.error(error);
      setMessage(els.firebaseMessage, getFriendlyAuthError(error), true);
    }
  }

  async function handleFirebaseLogout() {
    try {
      await signOut(auth);
      setMessage(els.firebaseMessage, "Logged out.", false);
    } catch (error) {
      console.error(error);
      setMessage(els.firebaseMessage, getFriendlyAuthError(error), true);
    }
  }

  async function loadCurrentUserSubmission() {
    if (!auth.currentUser) {
      currentUserSubmission = null;
      renderUserSubmission();
      updateSubmissionAvailability();
      return;
    }

    try {
      const submissionsRef = collection(db, "submissions");

      const q = query(
        submissionsRef,
        where("challengeSlug", "==", challengeSlug),
        where("userId", "==", auth.currentUser.uid),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        currentUserSubmission = null;
      } else {
        const docSnap = snap.docs[0];

        currentUserSubmission = {
          id: docSnap.id,
          ...docSnap.data()
        };
      }

      renderUserSubmission();
      updateSubmissionAvailability();
    } catch (error) {
      console.error(error);
      setMessage(els.submissionMessage, "Could not load your submission status.", true);
    }
  }

  function validateSubmissionFields() {
    const name = els.submissionName?.value?.trim() || "";
    const instagram = els.submissionInstagram?.value?.trim() || "";
    const photoTitle = els.submissionTitle?.value?.trim() || "";
    const photoDescription = els.submissionDescription?.value?.trim() || "";
    const file = els.submissionFile?.files?.[0];

    if (!name) return { ok: false, message: "Name is required." };
    if (!instagram) return { ok: false, message: "Instagram is required." };
    if (!photoTitle) return { ok: false, message: "Photo title is required." };
    if (!photoDescription) return { ok: false, message: "Photo description is required." };
    if (!file) return { ok: false, message: "Please choose a JPEG or PNG file." };

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      return { ok: false, message: "Only JPEG and PNG files are allowed." };
    }

    if (file.size > 10 * 1024 * 1024) {
      return { ok: false, message: "File must be smaller than 10 MB." };
    }

    return {
      ok: true,
      values: {
        name,
        instagram,
        photoTitle,
        photoDescription,
        file
      }
    };
  }

  async function handleSubmission(event) {
    event.preventDefault();

    if (!auth.currentUser) {
      setMessage(els.submissionMessage, "Sign in with your TLWC Community Account before submitting.", true);
      return;
    }

    if (!currentChallenge || !isChallengeSubmissionOpen()) {
      setMessage(els.submissionMessage, "Submissions are currently closed.", true);
      return;
    }

    if (currentUserSubmission) {
      setMessage(els.submissionMessage, "You have already submitted one photo for this challenge.", true);
      return;
    }

    const validation = validateSubmissionFields();

    if (!validation.ok) {
      setMessage(els.submissionMessage, validation.message, true);
      return;
    }

    const { name, instagram, photoTitle, photoDescription, file } = validation.values;

    try {
      els.submissionSubmit.disabled = true;
      setMessage(els.submissionMessage, "Uploading your photo and creating your submission...", false);

      const cleanedFileName = slugifyFileName(file.name);
      const imagePath = `challenge-submissions/${challengeSlug}/${auth.currentUser.uid}/${Date.now()}-${cleanedFileName}`;
      const storageRef = ref(storage, imagePath);

      await uploadBytes(storageRef, file, {
        contentType: file.type
      });

      const imageUrl = await getDownloadURL(storageRef);

      const docRef = await addDoc(collection(db, "submissions"), {
        challengeSlug,
        challengeRef: `challenges/${challengeSlug}`,
        userId: auth.currentUser.uid,

        displayName: name,
        instagramHandle: instagram,
        photoTitle,
        photoDescription,

        imagePath,
        imageUrl,
        fileName: file.name,
        fileType: file.type,

        status: "pending",
        voteCount: 0,
        createdAt: serverTimestamp()
      });

      currentUserSubmission = {
        id: docRef.id,
        challengeSlug,
        challengeRef: `challenges/${challengeSlug}`,
        userId: auth.currentUser.uid,

        displayName: name,
        instagramHandle: instagram,
        photoTitle,
        photoDescription,

        imagePath,
        imageUrl,
        fileName: file.name,
        fileType: file.type,

        status: "pending",
        voteCount: 0,
        createdAt: null
      };

      renderUserSubmission();
      updateSubmissionAvailability();

      setMessage(els.submissionMessage, "Submission received and sent for review.", false);

      els.submissionForm.reset();
    } catch (error) {
      console.error(error);
      setMessage(els.submissionMessage, `Submission failed: ${error.message}`, true);
    } finally {
      updateSubmissionAvailability();
    }
  }

  async function handleVoteClick(event) {
    const button = event.target.closest("[data-vote-button]");

    if (!button) return;

    const submissionId = button.dataset.submissionId;

    if (!submissionId) return;

    if (!auth.currentUser) {
      setMessage(els.voteMessage, "Sign in with your TLWC Community Account to vote.", true);
      return;
    }

    if (!currentChallenge || !isChallengeVotingOpen()) {
      setMessage(els.voteMessage, "Voting is currently closed.", true);
      return;
    }

    try {
      votingInProgressIds.add(submissionId);
      rerenderVoteButtons();

      setMessage(els.voteMessage, "Casting your vote...", false);

      const response = await castVoteCallable({
        challengeSlug,
        submissionId
      });

      const data = response.data || {};

      const nextEligibleDate = data.nextEligibleAtMillis
        ? new Date(data.nextEligibleAtMillis)
        : null;

      let message = "Your vote was recorded.";

      if (nextEligibleDate) {
        message += ` You can vote for this same photo again after ${nextEligibleDate.toLocaleString()}.`;
      }

      setMessage(els.voteMessage, message, false);

      await loadGalleryInitial();
    } catch (error) {
      console.error(error);

      const message =
        error?.message ||
        "Voting failed. Please try again.";

      setMessage(els.voteMessage, message, true);
    } finally {
      votingInProgressIds.delete(submissionId);
      rerenderVoteButtons();
    }
  }

  if (els.firebaseLoginBtn) {
    els.firebaseLoginBtn.addEventListener("click", handleFirebaseLogin);
  }

  if (els.firebaseSignupBtn) {
    els.firebaseSignupBtn.addEventListener("click", handleFirebaseSignupPrompt);
  }

  if (els.firebaseLogoutBtn) {
    els.firebaseLogoutBtn.addEventListener("click", handleFirebaseLogout);
  }

  if (els.passwordResetBtn) {
    els.passwordResetBtn.addEventListener("click", handlePasswordReset);
  }

  if (els.authModalConfirm) {
    els.authModalConfirm.addEventListener("click", async () => {
      if (typeof pendingModalAction === "function") {
        const action = pendingModalAction;
        closeAuthModal();
        await action();
      }
    });
  }

  if (els.authModalCloseBtns?.length) {
    els.authModalCloseBtns.forEach((button) => {
      button.addEventListener("click", closeAuthModal);
    });
  }

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && els.authModal && !els.authModal.hidden) {
      closeAuthModal();
    }
  });

  if (els.submissionForm) {
    els.submissionForm.addEventListener("submit", handleSubmission);
  }

  if (els.galleryLoadMore) {
    els.galleryLoadMore.addEventListener("click", loadGalleryMore);
  }

  if (els.galleryGrid) {
    els.galleryGrid.addEventListener("click", handleVoteClick);
  }

  onAuthStateChanged(auth, async (user) => {
    updateAuthPanels(user);

    if (user) {
      await loadCurrentUserSubmission();
    }
  });

  loadChallenge();

  window.TLWCChallengeApp = {
    app,
    auth,
    db,
    storage,
    functions,
    challengeSlug
  };
}